import React, { useState, useMemo } from "react";
import LayoutHOC from "../LayoutHOC";
import { Link } from "react-router-dom";
import usePromote from "../../hooks/usePromote";
import {
  useTable,
  useGlobalFilter,
  useFilters,
  usePagination,
  useSortBy,
} from "react-table";
import { DELETE_PROMOTE } from "../../utils/constants";
import { useSelector } from "react-redux";

const Promote = () => {
  const positions = usePromote();
  const [successMessage, setSuccessMessage] = useState("");
  const user = useSelector((state) => state.user);

  const handleDelete = async (id) => {
    const confirmDelete = window.confirm("Are you sure you want to delete this promotion type?");
    if (confirmDelete) {
      try {
        const { token } = user;
        const response = await fetch(`${DELETE_PROMOTE}${id}`, {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.ok) {
          setSuccessMessage("Promotion type deleted successfully!");
          setTimeout(() => {
            setSuccessMessage("");
            window.location.reload();
          }, 1500);
        } else {
          alert("Failed to delete. Please try again.");
        }
      } catch (error) {
        console.error("Error deleting:", error);
        alert("Failed to delete. Please try again.");
      }
    }
  };

  const columns = useMemo(
    () => [
      { Header: "English", accessor: "name", sortType: "alphanumeric" },
      { Header: "Hindi", accessor: "name_hindi", sortType: "alphanumeric" },
      { Header: "Marathi", accessor: "name_marathi", sortType: "alphanumeric" },
      { Header: "Punjabi", accessor: "name_punjabi", sortType: "alphanumeric" },
      {
        Header: "Actions",
        accessor: "id",
        Cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <Link
              to={`/edit_promote/${row.values.id}`}
              className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors text-sm font-medium"
            >
              <i className="bi bi-pencil"></i>
              Edit
            </Link>
            <button
              onClick={() => handleDelete(row.values.id)}
              className="inline-flex items-center gap-1 px-3 py-1.5 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors text-sm font-medium"
            >
              <i className="bi bi-trash"></i>
              Delete
            </button>
          </div>
        ),
      },
    ],
    []
  );

  const data = useMemo(() => positions?.data?.promote || [], [positions]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    state,
    setGlobalFilter,
    page,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    pageOptions,
    gotoPage,
    setPageSize,
  } = useTable(
    { columns, data, initialState: { pageSize: 10 } },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const { globalFilter, pageIndex, pageSize } = state;

  return (
    <main id="maincontent">
      <div className="p-6 mt-14 min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-gradient-to-br from-fuchsia-500 to-purple-600 rounded-xl shadow-lg">
                <i className="bi bi-megaphone text-white text-2xl"></i>
              </div>
              <div>
                <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
                  Promotions
                </h1>
                <p className="text-gray-500">Manage promotion types with multilingual support</p>
              </div>
            </div>
            <Link
              to="/create_promote"
              className="inline-flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-fuchsia-500 to-purple-500 text-white rounded-xl hover:from-fuchsia-600 hover:to-purple-600 transition-all duration-200 shadow-md hover:shadow-lg font-medium"
            >
              <i className="bi bi-plus-lg"></i>
              Add Promotion
            </Link>
          </div>
        </div>

        {successMessage && (
          <div className="mb-6 p-4 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center gap-3">
            <i className="bi bi-check-circle-fill text-emerald-500 text-xl"></i>
            <span className="text-emerald-700 font-medium">{successMessage}</span>
          </div>
        )}

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-4 border-b border-gray-100 flex flex-wrap items-center justify-between gap-4">
            <div className="relative">
              <i className="bi bi-search absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"></i>
              <input
                type="text"
                placeholder="Search promotions..."
                value={globalFilter || ""}
                onChange={(e) => setGlobalFilter(e.target.value)}
                className="pl-11 pr-4 py-2.5 border border-gray-200 rounded-xl w-72 focus:ring-2 focus:ring-fuchsia-500 focus:border-fuchsia-500 transition-all"
              />
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-500">Show</span>
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-fuchsia-500"
              >
                {[10, 25, 50, 100].map((size) => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
              <span className="text-sm text-gray-500">entries</span>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full" {...getTableProps()}>
              <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                {headerGroups.map((headerGroup) => (
                  <tr {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map((column) => (
                      <th
                        className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                        {...column.getHeaderProps(column.getSortByToggleProps())}
                      >
                        <div className="flex items-center gap-2">
                          {column.render("Header")}
                          <span className="text-gray-400">
                            {column.isSorted ? (column.isSortedDesc ? "↓" : "↑") : ""}
                          </span>
                        </div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody className="divide-y divide-gray-100" {...getTableBodyProps()}>
                {page.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length} className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <i className="bi bi-inbox text-4xl text-gray-300"></i>
                        <p className="text-gray-500">No promotions found</p>
                        <Link to="/create_promote" className="text-fuchsia-500 hover:underline">
                          Add your first promotion
                        </Link>
                      </div>
                    </td>
                  </tr>
                ) : (
                  page.map((row) => {
                    prepareRow(row);
                    return (
                      <tr className="hover:bg-fuchsia-50/50 transition-colors" {...row.getRowProps()}>
                        {row.cells.map((cell) => (
                          <td className="px-6 py-4 text-sm text-gray-700" {...cell.getCellProps()}>
                            {cell.render("Cell")}
                          </td>
                        ))}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {page.length > 0 && (
            <div className="p-4 border-t border-gray-100 flex flex-wrap items-center justify-between gap-4">
              <div className="text-sm text-gray-500">
                Showing <span className="font-medium">{pageIndex * pageSize + 1}</span> to{" "}
                <span className="font-medium">{Math.min((pageIndex + 1) * pageSize, data.length)}</span>{" "}
                of <span className="font-medium">{data.length}</span> results
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => gotoPage(0)} disabled={!canPreviousPage} className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                  <i className="bi bi-chevron-double-left"></i>
                </button>
                <button onClick={() => previousPage()} disabled={!canPreviousPage} className="px-4 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium">
                  Previous
                </button>
                <div className="flex items-center gap-1">
                  {pageOptions.slice(Math.max(0, pageIndex - 2), Math.min(pageOptions.length, pageIndex + 3)).map((pg) => (
                    <button
                      key={pg}
                      onClick={() => gotoPage(pg)}
                      className={`w-10 h-10 rounded-lg text-sm font-medium transition-colors ${pageIndex === pg ? "bg-fuchsia-500 text-white" : "hover:bg-gray-100 text-gray-600"}`}
                    >
                      {pg + 1}
                    </button>
                  ))}
                </div>
                <button onClick={() => nextPage()} disabled={!canNextPage} className="px-4 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium">
                  Next
                </button>
                <button onClick={() => gotoPage(pageOptions.length - 1)} disabled={!canNextPage} className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                  <i className="bi bi-chevron-double-right"></i>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </main>
  );
};

export default LayoutHOC(Promote);
